<template>
  <form-page-template
      :dirty="dirty"
      @save="doSave"
      fluid>
    <template #toolbar-items>
      <v-toolbar-title class="with-divider">
        データ実装
      </v-toolbar-title>

      <v-spacer></v-spacer>
      <v-btn
          @click="openDataSampleDialog"
          color="primary">
        <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
        サンプルレコード
      </v-btn>
    </template>

    <data-implementation-info
        :dataImplementation="dataImplementation"
        :editable="isEditable"
        @dirty="markDirty()">
    </data-implementation-info>

    <template #tab-items>
      <v-tab>データ項目実装</v-tab>
      <v-tab-item>
        <data-property-implementation-table
            :dataPropertyImplementations="dataPropertyImplementations">
        </data-property-implementation-table>
      </v-tab-item>

      <v-tab>
        プロセス手順実装
        <el-tooltip content="プロセス手順実装が登録されていません" placement="bottom" v-if="processOperationImplementationsView.length === 0">
          <error-icon level="warn" class="ml-1" small dark></error-icon>
        </el-tooltip>
      </v-tab>
      <v-tab-item>
        <process-operation-implementation-table
            :processOperationImplementations="processOperationImplementationsView"
            :dataImplementation="dataImplementationDetail"
            :serviceImplementations="serviceImplementationsView"
            :processImplementations="processImplementationsView">
        </process-operation-implementation-table>
      </v-tab-item>
    </template>

    <simple-dialog
        v-model="dialogDataSample"
        title-text="サンプルレコード"
        applyText=""
        @close="closeDataSampleDialog">
      <data-implementation-sample
          :dataImplementationId="dataImplementationId">
      </data-implementation-sample>
    </simple-dialog>
  </form-page-template>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import gql from "graphql-tag";
import BetterSet from "betterset";
import PathInfoMixin from "@/router/PathInfoMixin";
import IoablePageMixin from "@/router/IoablePageMixin";
import DataPropertyImplementationTable from "@/components/pages/implementation/DataImplementationShow/DataPropertyImplementationTable.vue";
import ProcessOperationImplementationTable from "@/components/pages/implementation/DataImplementationShow/ProcessOperationImplementationTable.vue";
import DataImplementationSample from "@/components/organisms/DataImplementationSample.vue";
import DataImplementationInfo from "@/components/organisms/DataImplementationInfo.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import ErrorIcon from "@/components/molecules/ErrorIcon.vue";
import FormPageTemplate from "@/components/templates/FormPageTemplate.vue";

export default {
  name: 'DataImplementationShow',
  components: {
    FormPageTemplate,
    ErrorIcon,
    DataPropertyImplementationTable,
    ProcessOperationImplementationTable,
    DataImplementationSample,
    DataImplementationInfo,
    SimpleDialog
  },
  mixins: [
    PathInfoMixin,
    IoablePageMixin
  ],
  props: {
    dataImplementationId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      dialogDataSample: false,
      dataImplementation: {},
    }
  },
  computed: {
    ...mapState('hcs/dataPropertyImplementation', ['dataPropertyImplementations']),
    ...mapState('hcs/serviceImplementation', ['serviceImplementations']),
    ...mapState('hcs/processImplementation', ['processImplementations']),
    ...mapState('hcs/processOperationImplementation', ['processOperationImplementations']),
    ...mapGetters({
      hcsClient: 'hcs/client',
      hdsEntities: 'hds/entities',
    }),
    dataImplementationDetail() {
      const dcm = this.hdsEntities.dataComponentModel.findOne({ id: this.dataImplementation.dataComponentModelId })
      return Object.assign({}, this.dataImplementation, {
        name: dcm?.name,
        description: dcm?.description
      });
    },
    serviceImplementationsView() {
      return this.serviceImplementations
        .filter(si => {
          return si.controllerId === this.dataImplementation.controllerId
        })
        .map(si => {
          const scm = this.hdsEntities.serviceComponentModel.findOne({id: si.serviceComponentModelId});
          return {
            ...si,
            serviceComponentModel: scm,
          };
        });
    },
    processImplementationsView() {
      return this.processImplementations.map(pi => {
        const pcm = this.hdsEntities.processComponentModel.findOne({id: pi.processComponentModelId});
        return {
          ...pi,
          processComponentModel: pcm,
        };
      });
    },
    processOperationImplementationsView() {
      return this.processOperationImplementations
        .filter(poi => poi.dataImplementationId === this.dataImplementationId)
        .map(poi => {
          const processImplementation = this.processImplementationsView.find(pi => pi.id === poi.processImplementationId);
          const serviceImplementation = this.serviceImplementationsView.find(si => si.id === processImplementation?.serviceImplementationId);
          return {
            ...poi,
            serviceComponentModelName: serviceImplementation?.serviceComponentModel?.name,
            processComponentModelName: processImplementation?.processComponentModel?.name,
            serviceImplementation: serviceImplementation || {},
            processImplementation: processImplementation || {},
          }
      })
    },
  },
  methods: {
    ...mapActions({
      gqlQueryAndLoad: 'hds/gqlQueryAndLoad'
    }),
    load() {
      return Promise.all([
        this.findDataImplementation(),
        this.findServiceImplementations(),
        this.findProcessImplementations(),
        this.$store.dispatch('hcs/processOperationImplementation/findList'),
      ])
    },
    save() {
      return this.hcsClient.patch(`/data_implementations/${this.dataImplementationId}`, {
        localId: this.dataImplementation.localId,
        remarks: this.dataImplementation.remarks,
        parameterDescription: this.dataImplementation.parameterDescription,
        publishing: this.dataImplementation.publishing
      });
    },
    openDataSampleDialog() {
      this.dialogDataSample = true
    },
    closeDataSampleDialog() {
      this.dialogDataSample = false
    },
    async findDataImplementation() {
      const dataImplementation = await this.$store.dispatch('hcs/dataImplementation/find', this.dataImplementationId);

      await this.$store.dispatch('hcs/dataImplementation/findDataPropertyImplementationList', dataImplementation.id);

      await this.gqlQueryAndLoad({
        query: gql`
            query FindDataComponentModel(
                $dataComponentModelId: ID!,
            ) {
                dataComponentModel: dataComponentModel(dataComponentModelId: $dataComponentModelId) {
                    id
                    name
                    description
                }
            }`,
        variables: {
          dataComponentModelId: dataImplementation.dataComponentModelId
        },
        mapping: {
          dataComponentModel: 'dataComponentModel*'
        }
      });

      this.dataImplementation = dataImplementation;
    },
    async findServiceImplementations() {
      const serviceImplementations = await this.$store.dispatch('hcs/serviceImplementation/findList')

      const serviceComponentModelIds = serviceImplementations
        .map(si => si.serviceComponentModelId)
        .reduce((set, value) => {set.add(value); return set;}, new BetterSet())
        .asArray()
        .filter(v => !!v);

      await this.gqlQueryAndLoad({
        query: gql`
            query FindServiceComponentModels(
                $serviceComponentModelIds: [ID!]!,
            ) {
                serviceComponentModel: serviceComponentModelInBulk(serviceComponentModelIds: $serviceComponentModelIds) {
                    id
                    name
                    description
                }
            }`,
        variables: {
          serviceComponentModelIds
        },
        mapping: {
          serviceComponentModel: 'serviceComponentModel*'
        }
      })
    },
    async findProcessImplementations() {
      let processImplementations = await this.$store.dispatch('hcs/processImplementation/findList')

      const processComponentModelIds = processImplementations
        .map(pi => pi.processComponentModelId)
        .reduce((set, value) => {set.add(value); return set;}, new BetterSet())
        .asArray()
        .filter(v => !!v);

      await this.gqlQueryAndLoad({
        query: gql`
            query FindProcessComponentModels(
                $processComponentModelIds: [ID!]!,
            ) {
                processComponentModel: processComponentModelInBulk(processComponentModelIds: $processComponentModelIds) {
                    id
                    name
                    description
                }
            }`,
        variables: {
          processComponentModelIds
        },
        mapping: {
          processComponentModel: 'processComponentModel*'
        }
      })
    }
  },
}
</script>
